import type { CardTemplateId } from '@shared-core/features/backend'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { GText } from '@shared-ui/components/ui/text'
import { useEffect } from 'react'
import { Image, type StyleProp, View, type ViewStyle } from 'react-native'
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { SvgXml } from 'react-native-svg'
import { cloudSources } from './clouds'
import { useCreateStyles } from './styles'

export type AppBankCardProps = {
  type?: CardTemplateId
  size?: 'small' | 'middle' | 'large'
  last4Digits?: string
  isNumberVisible?: boolean
  isCloudVisible?: boolean
  isBlocked?: boolean
  isFrozen?: boolean
  style?: StyleProp<ViewStyle>
  testID?: string
}

const artwork = { virtual: require('./assets/virtual.png'), platinum: require('./assets/platinum.png') } as const

export function AppBankCard({
  type = 'virtual',
  size = 'small',
  last4Digits,
  isNumberVisible = true,
  isCloudVisible = true,
  isFrozen = false,
  isBlocked = false,
  style,
  testID,
}: AppBankCardProps) {
  const styles = useCreateStyles()
  const frost = useSharedValue(isFrozen ? 1 : 0)
  useEffect(() => {
    frost.value = withTiming(isFrozen ? 1 : 0, { duration: isFrozen ? 1200 : 800, easing: Easing.inOut(Easing.cubic) })
  }, [frost, isFrozen])
  const frostStyle = useAnimatedStyle(() => ({ opacity: frost.value * 0.7 }))
  const isPlatinum = type === 'platinum'
  let cloudSource: string = cloudSources['cloud-middle']

  if (size === 'small') {
    cloudSource = cloudSources[type === 'virtual' ? 'cloud-virtual' : 'cloud-platinum']
  }
  return (
    <View
      testID={testID}
      style={[
        styles.card,
        isPlatinum && styles.platinum,
        size === 'middle' && styles.middle,
        size === 'large' && styles.large,
        style,
      ]}
    >
      <Image source={artwork[isPlatinum ? 'platinum' : 'virtual']} style={styles.cover} resizeMode="cover" />
      <Animated.Image
        source={require('./assets/frozen.png')}
        style={[styles.frozen, frostStyle]}
        resizeMode="cover"
        pointerEvents="none"
      />
      {isBlocked && (
        <View style={[styles.cloud, size === 'middle' && styles.cloudMiddle, size === 'large' && styles.cloudLarge]}>
          <AppIcon icon="lock-filled" size={size === 'small' ? 12 : 32} />
        </View>
      )}
      {!isBlocked && isCloudVisible && (
        <View style={[styles.cloud, size === 'middle' && styles.cloudMiddle, size === 'large' && styles.cloudLarge]}>
          <SvgXml xml={cloudSource} width={size === 'small' ? 12 : 32} height={size === 'small' ? 12 : 32} />
        </View>
      )}
      {size === 'small' && isNumberVisible && last4Digits && (
        <GText fw={500} style={[styles.number, isPlatinum && styles.numberPlatinum]}>
          {last4Digits}
        </GText>
      )}
    </View>
  )
}
